/** Format tampilan Rp — ribuan koma, desimal titik: Rp 200,000 / Rp 200,000.23 */
export function formatRp(value: number | null | undefined) {
  const n = Number(value) || 0;
  const neg = n < 0;
  const abs = Math.abs(n);
  const rounded = Math.round((abs + Number.EPSILON) * 100) / 100;
  const hasDec = Math.abs(rounded - Math.round(rounded)) >= 0.005;
  const body = rounded.toLocaleString('en-US', {
    minimumFractionDigits: hasDec ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${neg ? '-' : ''}Rp\u00A0${body}`;
}

/** Mask 3 digit terakhir jadi *** (mis. Rp 8,039.***) kecuali revealed */
export function formatRpMasked(value: number | null | undefined, revealed = false) {
  if (revealed) return formatRp(value);
  const n = Math.round((Math.abs(Number(value) || 0) + Number.EPSILON) * 100) / 100;
  const neg = (Number(value) || 0) < 0;
  const intPart = Math.floor(n);
  const s = String(intPart);
  if (s.length <= 3) return `${neg ? '-' : ''}Rp\u00A0***`;
  const head = s.slice(0, -3);
  const headFmt = Number(head).toLocaleString('en-US');
  return `${neg ? '-' : ''}Rp\u00A0${headFmt}.***`;
}

/** Angka biasa — 200,000 atau 200,000.23 */
export function formatNumber(value: number | null | undefined) {
  const n = Number(value) || 0;
  const rounded = Math.round((Math.abs(n) + Number.EPSILON) * 100) / 100;
  const neg = n < 0;
  const hasDec = Math.abs(rounded - Math.round(rounded)) >= 0.005;
  const body = rounded.toLocaleString('en-US', {
    minimumFractionDigits: hasDec ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${neg ? '-' : ''}${body}`;
}

export function accountLabel(a: { code: string; name: string }) {
  return `${a.code} — ${a.name}`;
}

export function todayISO() {
  return toLocalISODate(new Date());
}

/** YYYY-MM-DD in local timezone (hindari geser hari karena UTC) */
export function toLocalISODate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DMY_DATE_RE = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/;

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function isValidYmd(y: number, m: number, d: number) {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/**
 * Parse berbagai input tanggal → YYYY-MM-DD (storage).
 * Dukung: YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, Date, ISO datetime.
 */
export function parseToISODate(input: unknown, fallback?: string | null): string | null {
  if (input == null || input === '') return fallback ?? null;
  if (input instanceof Date && !Number.isNaN(input.getTime())) return toLocalISODate(input);

  const raw = String(input).trim();
  if (!raw) return fallback ?? null;

  const iso = ISO_DATE_RE.exec(raw.slice(0, 10));
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    if (isValidYmd(y, m, d)) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  const dmy = DMY_DATE_RE.exec(raw.slice(0, 10));
  if (dmy) {
    const d = Number(dmy[1]);
    const m = Number(dmy[2]);
    const y = Number(dmy[3]);
    if (isValidYmd(y, m, d)) return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  // Excel serial / long text: coba Date parser last
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return toLocalISODate(parsed);

  return fallback ?? null;
}

/** Tampil Tanggal/Bulan/Tahun → 07/08/2026 (input form / padat) */
export function formatDate(input: unknown, empty = '—') {
  const iso = parseToISODate(input);
  if (!iso) return empty;
  const [, y, m, d] = ISO_DATE_RE.exec(iso)!;
  return `${d}/${m}/${y}`;
}

const ID_MONTHS_LONG = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
] as const;

/** Tampil formal Indonesia → 27 Maret 2025 */
export function formatDateLong(input: unknown, empty = '—') {
  const iso = parseToISODate(input);
  if (!iso) return empty;
  const [, y, m, d] = ISO_DATE_RE.exec(iso)!;
  const day = Number(d);
  const month = ID_MONTHS_LONG[Number(m) - 1] || m;
  return `${day} ${month} ${y}`;
}

/** Rentang formal → 27 Maret 2025 → 31 Desember 2025 */
export function formatDateRangeLong(from: unknown, to: unknown, empty = '—') {
  const a = formatDateLong(from, '');
  const b = formatDateLong(to, '');
  if (!a && !b) return empty;
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} → ${b}`;
}

export function isFullYmd(v: unknown) {
  const iso = parseToISODate(v);
  return !!iso && ISO_DATE_RE.test(iso);
}

/** Tampilkan tanggal + jam lokal: 07/08/2026 21:00:21 */
export function formatDateTime(iso: string | null | undefined, fallbackDate?: string) {
  const raw = String(iso || '').trim();
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
    }
    if (raw.includes('T')) {
      const datePart = formatDate(raw.slice(0, 10));
      const timePart = raw.slice(11, 19);
      return timePart ? `${datePart} ${timePart}` : datePart;
    }
  }
  if (fallbackDate) return formatDate(fallbackDate);
  return '—';
}

/** Stamp bank: 07/08/2026 · 14:32:05 */
export function formatBankStamp(iso: string | null | undefined, fallbackDate?: string) {
  const raw = String(iso || '').trim();
  let d: Date | null = null;
  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }
  if (!d && fallbackDate) {
    const isoFb = parseToISODate(fallbackDate);
    if (isoFb) {
      const parsed = new Date(`${isoFb}T12:00:00`);
      if (!Number.isNaN(parsed.getTime())) d = parsed;
    }
  }
  if (!d) return '—';
  const hasTime = !!raw && (raw.includes('T') || /\d{2}:\d{2}/.test(raw));
  const datePart = formatDate(d);
  if (!hasTime) return datePart;
  return `${datePart} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function yearStartISO(d = new Date()) {
  return `${d.getFullYear()}-01-01`;
}

export type PeriodPresetKey =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'this_year'
  | 'last_7'
  | 'last_30'
  | 'last_90'
  | `months_ago_${number}`
  | 'custom';

export type PeriodRange = { from: string; to: string };

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0);
}

function startOfMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex, 1);
}

/** Senin sebagai awal minggu (ID) */
function startOfWeekMonday(d: Date) {
  const x = startOfDay(d);
  const day = x.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? 6 : day - 1;
  x.setDate(x.getDate() - diff);
  return x;
}

export function getPeriodRange(key: PeriodPresetKey, now = new Date()): PeriodRange {
  const today = startOfDay(now);

  if (key === 'today') {
    const iso = toLocalISODate(today);
    return { from: iso, to: iso };
  }
  if (key === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const iso = toLocalISODate(y);
    return { from: iso, to: iso };
  }
  if (key === 'this_week') {
    return { from: toLocalISODate(startOfWeekMonday(today)), to: toLocalISODate(today) };
  }
  if (key === 'this_month') {
    return {
      from: toLocalISODate(startOfMonth(today.getFullYear(), today.getMonth())),
      to: toLocalISODate(today),
    };
  }
  if (key === 'this_year') {
    return {
      from: toLocalISODate(startOfMonth(today.getFullYear(), 0)),
      to: toLocalISODate(today),
    };
  }
  if (key === 'last_7' || key === 'last_30' || key === 'last_90') {
    const days = key === 'last_7' ? 7 : key === 'last_30' ? 30 : 90;
    const start = new Date(today);
    start.setDate(start.getDate() - (days - 1));
    return { from: toLocalISODate(start), to: toLocalISODate(today) };
  }

  const m = /^months_ago_(\d+)$/.exec(key);
  if (m) {
    const n = Number(m[1]);
    const ref = new Date(today.getFullYear(), today.getMonth() - n, 1);
    const from = startOfMonth(ref.getFullYear(), ref.getMonth());
    const to = endOfMonth(ref.getFullYear(), ref.getMonth());
    return { from: toLocalISODate(from), to: toLocalISODate(to) };
  }

  // custom / unknown — keep caller values; return today as safe fallback
  const iso = toLocalISODate(today);
  return { from: iso, to: iso };
}

/**
 * Periode sebelumnya untuk bandingkan Δ% KPI.
 * this_year / this_month → kalender-aligned; lainnya → geser mundur sepanjang window.
 */
export function getPreviousPeriodRange(fromIso: string, toIso: string): PeriodRange {
  const from = String(fromIso || '').slice(0, 10);
  const to = String(toIso || '').slice(0, 10);
  const fm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(from);
  const tm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(to);
  if (!fm || !tm) {
    const prev = getPeriodRange('months_ago_1');
    return prev;
  }
  const fromD = new Date(Number(fm[1]), Number(fm[2]) - 1, Number(fm[3]));
  const toD = new Date(Number(tm[1]), Number(tm[2]) - 1, Number(tm[3]));
  const days = Math.max(0, Math.round((toD.getTime() - fromD.getTime()) / 86400000));

  // Tahun penuh kalender (1 Jan → …) → tahun sebelumnya
  if (Number(fm[2]) === 1 && Number(fm[3]) === 1 && Number(tm[2]) === 12) {
    const y = Number(fm[1]) - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  // Tahun ini (1 Jan → hari ini) → tahun lalu sampai tanggal sama
  if (Number(fm[2]) === 1 && Number(fm[3]) === 1) {
    const y = Number(fm[1]) - 1;
    const last = endOfMonth(y, Number(tm[2]) - 1).getDate();
    const day = Math.min(Number(tm[3]), last);
    return {
      from: `${y}-01-01`,
      to: `${y}-${String(tm[2]).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    };
  }
  // Bulan ini (1 → hari ini) → bulan lalu penuh sampai hari sama
  if (Number(fm[3]) === 1 && Number(fm[1]) === Number(tm[1]) && Number(fm[2]) === Number(tm[2])) {
    let y = Number(fm[1]);
    let mi = Number(fm[2]) - 2;
    if (mi < 0) {
      mi = 11;
      y -= 1;
    }
    const last = endOfMonth(y, mi).getDate();
    const day = Math.min(Number(tm[3]), last);
    return {
      from: toLocalISODate(startOfMonth(y, mi)),
      to: toLocalISODate(new Date(y, mi, day)),
    };
  }

  // Generic: geser mundur sepanjang window yang sama
  const prevTo = new Date(fromD);
  prevTo.setDate(prevTo.getDate() - 1);
  const prevFrom = new Date(prevTo);
  prevFrom.setDate(prevFrom.getDate() - days);
  return { from: toLocalISODate(prevFrom), to: toLocalISODate(prevTo) };
}

/**
 * Range 1 → hari-ini (nomor tanggal) di bulan terpilih.
 * Contoh: hari ini 20 Sep → pilih Maret → 2026-03-01 s/d 2026-03-20.
 * Hari diklem ke akhir bulan (Feb 28/29).
 */
export function getMonthToTodayRange(
  monthIndex: number,
  now = new Date(),
  year?: number
): PeriodRange {
  const y = year != null && Number.isFinite(year) ? Number(year) : now.getFullYear();
  const mi = Math.max(0, Math.min(11, Math.floor(monthIndex)));
  const day = now.getDate();
  const last = endOfMonth(y, mi).getDate();
  const toDay = Math.min(day, last);
  const from = startOfMonth(y, mi);
  const to = new Date(y, mi, toDay);
  return { from: toLocalISODate(from), to: toLocalISODate(to) };
}

/** Deteksi bulan (0–11) jika from=tanggal 1 dan to=hari-ini di bulan yang sama. */
export function detectMonthToToday(
  fromIso: string,
  toIso: string,
  now = new Date()
): { monthIndex: number; year: number } | null {
  const from = String(fromIso || '').slice(0, 10);
  const to = String(toIso || '').slice(0, 10);
  const fm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(from);
  const tm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(to);
  if (!fm || !tm) return null;
  const y = Number(fm[1]);
  const mi = Number(fm[2]) - 1;
  if (Number(fm[3]) !== 1) return null;
  if (Number(tm[1]) !== y || Number(tm[2]) - 1 !== mi) return null;
  const expected = getMonthToTodayRange(mi, now, y);
  if (expected.to !== to) return null;
  return { monthIndex: mi, year: y };
}

const ID_MONTHS = ID_MONTHS_LONG;

export function periodPresetOptions(
  now = new Date(),
  t?: (key: string, params?: Record<string, string | number>) => string
) {
  const y = now.getFullYear();
  const tr =
    t ||
    ((key: string, params?: Record<string, string | number>) => {
      // fallback ID jika dipanggil tanpa i18n
      const idFallback: Record<string, string> = {
        'period.today': 'Hari Ini',
        'period.yesterday': 'Kemarin',
        'period.thisWeek': 'Minggu Ini',
        'period.thisMonth': 'Bulan Ini',
        'period.thisYear': 'Tahun Ini ({year})',
        'period.last7': '7 Hari',
        'period.last30': '30 Hari',
        'period.last90': '90 Hari',
        'period.monthsAgo1': '1 Bulan Lalu ({month} {year})',
        'period.monthsAgoN': '{n} Bulan Lalu ({month} {year})',
        'period.custom': 'Kustom (manual)',
      };
      let s = idFallback[key] || key;
      if (params) {
        for (const [k, v] of Object.entries(params)) s = s.replace(`{${k}}`, String(v));
      }
      return s;
    });

  const monthName = (idx: number) => tr(`month.${idx}`);

  const items: { value: PeriodPresetKey; title: string }[] = [
    { value: 'today', title: tr('period.today') },
    { value: 'yesterday', title: tr('period.yesterday') },
    { value: 'this_week', title: tr('period.thisWeek') },
    { value: 'this_month', title: tr('period.thisMonth') },
    { value: 'last_7', title: tr('period.last7') },
    { value: 'last_30', title: tr('period.last30') },
    { value: 'last_90', title: tr('period.last90') },
    { value: 'this_year', title: tr('period.thisYear', { year: y }) },
  ];
  for (let n = 1; n <= 12; n++) {
    const ref = new Date(now.getFullYear(), now.getMonth() - n, 1);
    const label =
      n === 1
        ? tr('period.monthsAgo1', { month: monthName(ref.getMonth()), year: ref.getFullYear() })
        : tr('period.monthsAgoN', {
            n,
            month: monthName(ref.getMonth()),
            year: ref.getFullYear(),
          });
    items.push({ value: `months_ago_${n}` as PeriodPresetKey, title: label });
  }
  items.push({ value: 'custom', title: tr('period.custom') });
  return items;
}

